import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { EmberChangeset } from 'ember-changeset';
import t from 'ember-intl/helpers/t';
import and from 'ember-truth-helpers/helpers/and';
import eq from 'ember-truth-helpers/helpers/eq';
import { type Signature as ForcSignature } from 'parog-web/components/forc/provider/base';
import icd10Title from 'parog-web/helpers/icd10-title';
import type Patient from 'parog-web/models/patient';
import type ICD10Service from 'parog-web/services/icd10';
import type SemeiaSessionService from 'parog-web/services/session';

interface Args {
  patient: Patient;
  changeset: EmberChangeset;
  form: ForcSignature['Blocks']['default'][0];
}
export default class ICD10Dropdown extends Component<Args> {
  @service declare icd10: ICD10Service;
  @service declare session: SemeiaSessionService;

  get suggestionsForFur(): string[] {
    const suggestions = this.icd10.suggestedIcd10ForFur(
      this.args.patient.followUpReason || ''
    );

    /**
     * If there are no suggested ICD10 codes for the current
     * patient's FuR, don't filter the suggestions array
     */
    if (!suggestions.length) return suggestions;

    const existingComorbidities = this.args.changeset.get('comorbidities');
    const existingIgnoredComorbidities = this.args.changeset.get(
      'ignoredComorbidities'
    );

    /**
     * If there are existing comorbidities or ignored comorbidities
     * for the current patient, don't filter the suggestions array
     */
    if (!existingComorbidities && !existingIgnoredComorbidities.length)
      return suggestions;

    /**
     * Filter the suggestions if some have already been ignored
     * or added to the comorbidity list
     */
    return suggestions.reduce((acc: string[], curr) => {
      if (
        existingComorbidities?.includes(curr) ||
        existingIgnoredComorbidities.includes(curr)
      ) {
        return acc;
      }

      acc.push(curr);
      return acc;
    }, []);
  }

  addSuggestedComorbidity = (icd10Code: string, event: MouseEvent): void => {
    const comorbidities = this.args.changeset.get('comorbidities')
      ? [...this.args.changeset.get('comorbidities')]
      : [];

    comorbidities.push(icd10Code);
    this.args.changeset.set('comorbidities', comorbidities);
    event.preventDefault();
  };

  ignoreSuggestedComorbidity = (icd10Code: string, event: MouseEvent): void => {
    const ignoredComorbidities = this.args.changeset.get('ignoredComorbidities')
      ? [...this.args.changeset.get('ignoredComorbidities')]
      : [];

    ignoredComorbidities.push(icd10Code);
    this.args.changeset.set('ignoredComorbidities', ignoredComorbidities);
    event.preventDefault();
  };

  <template>
    {{#if (and this.session.medic this.suggestionsForFur)}}
      <span class='italic text-[#808080]'>Le renseignement des comorbidités a un
        impact sur le bon suivi de votre patient.</span>
      {{#if (eq @patient.followUpReason 'diabeto-dt2')}}
        <span>
          <br />
          <strong>
            Dans le cadre des critères d'inclusion de l'étude, le patient doit
            avoir au moins une comorbidité.
            <br />
            Si vous ne savez pas si votre patient a l’une de ces comorbidités,
            ne cliquez pas sur le champs “oui” ou “non”.
          </strong>
        </span>
      {{/if}}
      <div class='ui message' data-comorbidities-section>
        <span>Votre patient souffre-t-il des comorbidités suivantes ?</span>
        <ul>
          {{#each this.suggestionsForFur as |suggestion|}}
            <li class='[&:not(:first-child)>div:first]:pt-2'>
              <div
                class='grid grid-cols-[1.5fr_0.5fr_0.5fr_1fr] [.ui.message_li:not(:first-child)_&]:pt-[0.5em]'
              >
                <span class='mr16'>{{t
                    (concat 'comorbidity.icd10.' suggestion)
                  }}</span>
                <button
                  class='ui mini primary button mr16 px-4 py-2'
                  type='button'
                  data-test-add-button={{suggestion}}
                  {{on 'click' (fn this.addSuggestedComorbidity suggestion)}}
                >
                  Oui
                </button>
                <button
                  class='ui mini primary button mr16 px-4 py-2'
                  type='button'
                  data-test-ignore-button={{suggestion}}
                  {{on 'click' (fn this.ignoreSuggestedComorbidity suggestion)}}
                >
                  Non
                </button>
              </div>
              {{#if (eq suggestion 'I51.6')}}
                <div class='mx-2 my-2 max-w-[50%] text-justify text-[smaller]'>
                  Maladies des coronaires (IDM, agor avec preuve coronaire,
                  sténose de coronaire, carotide ou a. périphérique > 50%),
                  maladies cérébrovasculaires (AVC ischémique, AIT, sténose
                  carotidienne) AOMI (ATCD de revascularisation, amputation ou
                  claudication avec preuve d’imagerie d’origine vasculaire)
                </div>
              {{/if}}
            </li>
          {{/each}}
        </ul>
      </div>
    {{/if}}

    <@form.Dropdown
      @allowAdditions={{true}}
      @labelName='Comorbidités'
      @propertyName='comorbidities'
      @multipleSelection={{true}}
      as |dropdown|
    >
      {{#each-in this.icd10.release2008 as |blockCode categoryCodes|}}
        <dropdown.Divider>{{icd10Title blockCode}}</dropdown.Divider>

        {{#each categoryCodes as |categoryCode|}}
          <dropdown.Item @key={{categoryCode}}>
            {{icd10Title categoryCode}}
          </dropdown.Item>
        {{/each}}
      {{/each-in}}
    </@form.Dropdown>
  </template>
}
